import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Users as UsersIcon } from "lucide-react";

export default function Lists() {
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const nav = useNavigate();

  const load = () => api.get("/lists").then((r) => setLists(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/lists", form);
      toast.success("List created");
      setOpen(false); setForm({ name: "", description: "" });
      nav(`/lists/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete list and all its contacts?")) return;
    try { await api.delete(`/lists/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="p-8 lg:p-10 max-w-[1400px] cf-fade">
      <div className="flex items-end justify-between mb-8 pb-6 border-b border-zinc-800 flex-wrap gap-4">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// audiences</div>
          <h1 className="cf-page-title mt-2">Contact Lists</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="create-list-button" className="cf-btn-primary"><Plus size={14} /> Create List</button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border-zinc-800 rounded-none text-white">
            <DialogHeader><DialogTitle className="font-display uppercase tracking-tight text-2xl">New List</DialogTitle></DialogHeader>
            <form onSubmit={onCreate} className="space-y-4 mt-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">List name</label>
                <input data-testid="new-list-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" placeholder="Q1 SaaS Leads" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Description</label>
                <textarea data-testid="new-list-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="cf-input" rows={3} />
              </div>
              <DialogFooter className="pt-4">
                <button type="submit" data-testid="submit-create-list" className="cf-btn-primary w-full justify-center">Create List</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {lists.length === 0 ? (
        <div className="cf-card p-16 text-center">
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-600">// no lists yet — create one to import contacts</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
          {lists.map((l) => (
            <div key={l.id} className="bg-[#0A0A0A] p-6 hover:bg-zinc-900/40 transition-colors group" data-testid={`list-card-${l.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// list</div>
                <button onClick={(e) => { e.stopPropagation(); onDelete(l.id); }} data-testid={`delete-list-${l.id}`} className="text-zinc-700 hover:text-[#E23A28] opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={14} /></button>
              </div>
              <Link to={`/lists/${l.id}`} className="block min-w-0">
                <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-2 line-clamp-2 break-words" title={l.name}>{l.name}</h3>
                <p className="text-sm text-zinc-500 mb-6 line-clamp-2 min-h-[40px] break-words">{l.description || "—"}</p>
                <div className="flex items-center justify-between font-mono text-xs text-zinc-400 border-t border-zinc-800 pt-4 gap-2">
                  <div className="flex items-center gap-2 flex-shrink-0"><UsersIcon size={12} /><span>{l.contact_count} contacts</span></div>
                  {l.owner_name && <span className="text-zinc-600 truncate min-w-0" title={`by ${l.owner_name}`}>by {l.owner_name}</span>}
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
